import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Clock, CheckCircle2, XCircle, AlertTriangle, Check, X, ChevronRight } from 'lucide-react';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';
import { setQueryData } from '@/lib/query';
import { cn } from '@/lib/utils';
import type { ChatMessage } from '@/hooks/useProduction';
import { chatKey, useProductionChat, type ChatData } from '@/hooks/useProductionChat';

interface WorkQueueProps {
  productionId: string;
  running: boolean;
  // Live chat messages (from state.chat) — used to refresh promptly on new events.
  liveMessages: ChatMessage[];
  // Reports the shot_ids with a rework/reshoot still pending (awaiting OK / queued /
  // executing) so the galleries can mark those tiles as already-flagged.
  onPendingTargets?: (targets: Set<string>) => void;
  // Normalized "type:ID" keys of QC-ACCEPTED assets (state.finalAssets). A rework the
  // queue marks "done" fired the regeneration, but the new asset still has to clear the
  // identity/location gates — until its key lands here it's still iterating, so we don't
  // flash a premature "Done".
  finalAssets?: Set<string>;
}

// Producer-confirmed work only (awaiting_confirm is still the user's to decide).
// Labels come from the `production` catalogue keyed `queue.status.<key>`.
const STATUS_META: Record<string, { cls: string; icon: typeof Clock }> = {
  confirmed: { cls: 'text-amber-600 border-amber-500/40', icon: Clock },
  executing: { cls: 'text-blue-600 border-blue-500/40', icon: Loader2 },
  done: { cls: 'text-green-600 border-green-500/40', icon: CheckCircle2 },
  error: { cls: 'text-red-600 border-red-500/40', icon: AlertTriangle },
  rejected: { cls: 'text-muted-foreground border-border', icon: XCircle },
  awaiting_confirm: { cls: 'text-purple-600 border-purple-500/40', icon: Clock },
  // Synthetic status: the action ran but the regenerated asset hasn't cleared QC yet.
  iterating: { cls: 'text-blue-600 border-blue-500/40', icon: Loader2 },
};

// The QC-acceptance key (state.finalAssets) for a rework action's target, or '' for
// action types that don't produce a gated asset (e.g. rescore_music).
const FINAL_PREFIX: Record<string, string> = {
  redesign_character: 'character',
  redesign_location: 'location',
  rework_keyframe: 'keyframe',
  reshoot_clip: 'clip',
};
function finalKeyForItem(it: QueueItem): string {
  const prefix = FINAL_PREFIX[it.type];
  if (!prefix || !it.target) return '';
  return `${prefix}:${it.target.toUpperCase().replace(/\s+/g, '_').replace(/\./g, '')}`;
}

interface QueueItem {
  id: string;
  status: string;
  type: string;
  target: string;
  summary: string;
}

// Map an action to the asset it touches, so the queue can preview it. `bust` swaps
// the tile to the regenerated version once a rework has run.
function assetPreview(
  productionId: string,
  it: QueueItem,
  bust: number,
): { kind: 'image' | 'video' | 'audio'; url: string } | null {
  const base = `/api/assets/${productionId}`;
  const t = it.target;
  if (!t) return null;
  const safe = t.replace(/ /g, '_').replace(/\./g, '').toUpperCase();
  if (it.type === 'rework_keyframe') {
    return { kind: 'image', url: `${base}/keyframes/${t}.png?v=${bust}` };
  }
  if (it.type === 'redesign_character') {
    return { kind: 'image', url: `${base}/characters/${safe}.png?v=${bust}` };
  }
  if (it.type === 'redesign_location') {
    return { kind: 'image', url: `${base}/locations/${safe}.png?v=${bust}` };
  }
  if (it.type === 'reshoot_clip') {
    return { kind: 'video', url: `${base}/clips/${t}.mp4?v=${bust}` };
  }
  if (it.type === 'rescore_music') {
    return { kind: 'audio', url: `${base}/audio/music/${t}.mp3?v=${bust}` };
  }
  return null;
}

function AssetThumb({ preview }: { preview: { kind: string; url: string } }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  const cls = 'w-20 shrink-0 rounded border border-border bg-black';
  if (preview.kind === 'image') {
    const t = `${preview.url}${preview.url.includes('?') ? '&' : '?'}thumb=1`;
    return <img src={t} alt="asset" className={`${cls} aspect-video object-cover`} onError={() => setFailed(true)} />;
  }
  if (preview.kind === 'video') {
    return (
      <video
        src={preview.url}
        className={`${cls} aspect-video`}
        muted
        playsInline
        onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
        onMouseLeave={(e) => {
          const v = e.target as HTMLVideoElement;
          v.pause();
          v.currentTime = 0;
        }}
        onError={() => setFailed(true)}
      />
    );
  }
  return <audio src={preview.url} controls className="h-7 w-40 shrink-0" onError={() => setFailed(true)} />;
}

const OPEN_KEY = 'se_queue_open';

/** Remembered across visits, like the Director's Log. */
function initialOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

export default function WorkQueue({ productionId, running, liveMessages, onPendingTargets, finalAssets }: WorkQueueProps) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(initialOpen);
  // Shared + cached with the review gate (one /api/chat poll per production).
  const { data, updatedAt, refetch } = useProductionChat(productionId, running);
  // Bumped on every refresh so a reworked asset's thumbnail swaps to the new version.
  const bust = updatedAt || 1;
  const items: QueueItem[] = useMemo(
    () =>
      (data?.messages || [])
        .filter((m) => m.kind === 'proposal')
        .map((m) => {
          const action = m.action || {};
          return {
            id: m.id,
            status: m.status || 'confirmed',
            type: action.type || 'action',
            target: action.target || '',
            summary: action.summary || m.text || '',
          };
        }),
    [data],
  );

  const setStatus = useCallback(
    (messageId: string, status: string) =>
      setQueryData<ChatData>(chatKey(productionId), (prev) => ({
        messages: (prev?.messages || []).map((m) => (m.id === messageId ? { ...m, status } : m)),
      })),
    [productionId],
  );

  const confirm = useCallback(
    async (messageId: string, approve: boolean) => {
      // Optimistic flip so the buttons resolve immediately.
      setStatus(messageId, approve ? 'confirmed' : 'rejected');
      try {
        await apiFetch(`/api/chat/${productionId}/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_id: messageId, approve }),
        });
      } catch {
        setStatus(messageId, 'awaiting_confirm');
      }
    },
    [productionId, setStatus],
  );

  // Refresh when new chat events arrive over the progress stream (polling while
  // running is handled by the shared query).
  useEffect(() => {
    if (liveMessages.length) refetch().catch(() => {});
  }, [refetch, liveMessages.length]);

  // Tell the page which assets still have a rework/reshoot/redesign pending, so their
  // tiles get the "already flagged" marker. Keys are normalized (upper + underscores)
  // because redesign targets may be display names while tiles use file names.
  useEffect(() => {
    if (!onPendingTargets) return;
    const PENDING_TYPES = new Set(['rework_keyframe', 'reshoot_clip', 'redesign_character', 'redesign_location']);
    const targets = new Set<string>();
    for (const it of items) {
      const pendingStatus = it.status === 'awaiting_confirm' || it.status === 'confirmed' || it.status === 'executing';
      if (pendingStatus && it.target && PENDING_TYPES.has(it.type)) {
        targets.add(it.target.toUpperCase().replace(/\s+/g, '_').replace(/\./g, ''));
      }
    }
    onPendingTargets(targets);
  }, [items, onPendingTargets]);

  if (items.length === 0) return null;

  // Order: active work first, then done/dismissed.
  const rank = (s: string) => ({ executing: 0, confirmed: 1, awaiting_confirm: 2, error: 3, done: 4, rejected: 5 })[s] ?? 9;
  const sorted = [...items].sort((a, b) => rank(a.status) - rank(b.status));
  const pending = items.filter((i) => i.status === 'confirmed' || i.status === 'executing').length;
  // Nothing in this queue moves until these are confirmed, so the collapsed header
  // has to say so — otherwise closing the panel silently stalls the rework.
  const needsOk = items.filter((i) => i.status === 'awaiting_confirm').length;

  const toggle = () => {
    setOpen((o) => {
      try {
        localStorage.setItem(OPEN_KEY, o ? '0' : '1');
      } catch {
        /* no-storage — the panel just won't remember */
      }
      return !o;
    });
  };

  return (
    <Card className={cn(needsOk > 0 && 'border-primary/50')}>
      {/* Disclosure triangle + label is the toggle; the help dot and the badges are
          SIBLINGS of it, not children. HelpDot renders its own <button> and CardTitle
          an <h3>, neither of which may legally nest inside a button — and a nested
          help button would have fired the toggle on click. */}
      <CardHeader className="p-0">
        <div className="flex items-center gap-2 px-6 py-3">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            title={open ? t('queue.collapse') : t('queue.expand')}
            className="flex min-w-0 items-center gap-1.5 text-left"
          >
            <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-primary transition-transform', open && 'rotate-90')} />
            <CardTitle className="text-sm font-medium">{t('queue.title')}</CardTitle>
          </button>
          <HelpDot>{t('queue.help')}</HelpDot>
          {needsOk > 0 && <Badge className="text-xs">{t('queue.needsOk', { count: needsOk })}</Badge>}
          <Badge variant={pending ? 'default' : 'outline'} className="ml-auto text-xs">
            {t('queue.pending', { count: pending })}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className={cn(!open && 'hidden')}>
        <div className="space-y-1.5">
          {sorted.map((it) => {
            // A "done" rework whose regenerated asset hasn't re-cleared QC (its key isn't
            // in finalAssets yet) is still churning — show that, not a premature "Done".
            const fkey = finalKeyForItem(it);
            const effStatus = it.status === 'done' && running && fkey && !finalAssets?.has(fkey) ? 'iterating' : it.status;
            const meta = STATUS_META[effStatus] || STATUS_META.confirmed;
            const Icon = meta.icon;
            const preview = assetPreview(productionId, it, bust);
            return (
              <div key={it.id} className="flex items-start gap-2 text-xs">
                <Icon
                  className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.cls.split(' ')[0]} ${
                    it.status === 'executing' || effStatus === 'iterating' ? 'animate-spin' : ''
                  }`}
                />
                {preview && <AssetThumb preview={preview} />}
                <div className="min-w-0 flex-1">
                  <div className="truncate">{it.summary || `${it.type}${it.target ? ` · ${it.target}` : ''}`}</div>
                  {it.target && it.summary && <div className="font-mono text-[10px] text-muted-foreground">{it.target}</div>}
                </div>
                {it.status === 'awaiting_confirm' ? (
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" className="h-6 px-2 text-[10px]" onClick={() => confirm(it.id, true)}>
                      <Check className="mr-0.5 h-3 w-3" />
                      {t('queue.ok')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-[10px]"
                      title={t('queue.dismiss')}
                      aria-label={t('queue.dismiss')}
                      onClick={() => confirm(it.id, false)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <span className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}>
                    {t(`queue.status.${effStatus}`, { defaultValue: effStatus })}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
